import {
  Form,
  Row,
  Col,
  Button,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { countries } from "countries-list";
import { youthMembershipSchema } from "../schemas/Schema";
import { YouthMembershipFormData } from "../../types/UserData";
import { useNavigate } from "react-router-dom";
import SelectInput from "../common/SelectInput";
import { useSaveUser } from "../../api/mutations/useSaveUser";
import { useQueryClient } from "@tanstack/react-query";
import { ImageUrls } from "../../types/Types";
import { toast } from "react-toastify";
import { useGetUser } from "../../api/queries/useGetUser";
import { useAppContext } from "../../Context/AppContext";
import { SectionHeader } from "../Registration/YouthMembershipForm";
import ImageCropperModal from "../common/ImageCropperModal";
import { SetStateAction, useState } from "react";
import { universities } from "./Universities";

const renderTooltip = (msg: string) => <Tooltip id="tooltip">{msg}</Tooltip>;

interface YouthMembershipFormProps {
  defaultData?: YouthMembershipFormData;
  showEditModal?: boolean;
  setShowEditModal?: (show: boolean) => void;
  defaultImages?: ImageUrls;
  completionPercent?: number;
}

const EditYouthData = ({
  defaultData,
  defaultImages,
  showEditModal,
  setShowEditModal,
  completionPercent,
}: YouthMembershipFormProps) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm({
    resolver: yupResolver(youthMembershipSchema),
    defaultValues: {
      ...defaultData,
      phoneNumber: defaultData?.phoneNumber ?? "",
      professionalPhoto: defaultImages?.ProfessionalPhoto ?? "",
      idDocument: defaultImages?.IdDocument ?? "",
    },
  });
  const [showCropper, setShowCropper] = useState(false);
  const [tempImage, setTempImage] = useState<string | null>(null);
  const [croppedPhotoFile, setCroppedPhotoFile] = useState<File | null>(null);
  const [selectedUniversity, setSelectedUniversity] = useState(
    defaultData?.universityCollege || ""
  );
  const navigate = useNavigate();
  const objectId = localStorage.getItem("objectId");
  const queryClient = useQueryClient();
  const { user: currentUser } = useAppContext();
  const isAdmin = currentUser?.user?.isAdmin === 1;
  const disabledField =
    showEditModal && !isAdmin && completionPercent && completionPercent >= 100;
  const hasOther =
    defaultData?.universityCollege &&
    !universities.includes(defaultData.universityCollege);
  const { mutate: updateUser, isPending } = useSaveUser({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["user", objectId] });
      if (showEditModal) {
        setShowEditModal?.(false);
      }
      if (showEditModal) {
        setShowEditModal?.(false);
      }
      if (!defaultData) {
        navigate("/my-account");
      }
    },
    onError: () => {
      toast.error("Error while registering user. Please try again.");
    },
  });

  const handleUniversityChange = (e: {
    target: { value: SetStateAction<string> };
  }) => {
    setSelectedUniversity(e.target.value);
  };

  const { data: userData } = useGetUser({ id: objectId || "" });

  const onSubmit = (data: YouthMembershipFormData) => {
    if (errors.professionalPhoto || errors.idDocument) {
      if (errors.professionalPhoto) {
        setError("professionalPhoto", {
          type: "manual",
          message: errors.professionalPhoto.message,
        });
      }
      if (errors.idDocument) {
        setError("idDocument", {
          type: "manual",
          message: errors.idDocument.message,
        });
      }

      return;
    }

    // Create FormData object
    const formData = new FormData();

    // Merge regular data
    const mergedData = {
      ...userData?.user,
      ...data,
      dateOfBirth: data.dateOfBirth.toISOString(),
      hasCompletedProfile: 1,
      phoneNumber: data.phoneNumber || "",
      skillsExperience: data.skillsExperience || "",
    };

    // Append all non-file fields to FormData
    Object.entries(mergedData).forEach(([key, value]) => {
      if (
        value !== null &&
        value !== undefined &&
        key !== "idDocument" &&
        key !== "professionalPhoto"
      ) {
        if (typeof value === "object") {
          formData.append(key, JSON.stringify(value));
        } else {
          formData.append(key, String(value));
        }
      }
    });

    // Append file fields
    const idDocumentFile = (
      document.getElementById("idUpload") as HTMLInputElement
    )?.files?.[0];
    if (idDocumentFile) {
      formData.append("idDocument", idDocumentFile);
    }

    if (croppedPhotoFile) {
      formData.append("professionalPhoto", croppedPhotoFile);
    }

    // Store merged data in localStorage (without files)
    localStorage.setItem("UserProfile", JSON.stringify(mergedData));

    // Send FormData to API
    updateUser(formData);
  };

  const countryOptions = Object.entries(countries)
    .map(([code, country]) => ({
      value: country.name,
      label: country.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const languageOptions = [
    { value: "English", label: "English" },
    { value: "Arabic", label: "Arabic" },
    { value: "Turkish", label: "Turkish" },
    { value: "Bahasa Indonesia", label: "Bahasa Indonesia" },
    { value: "French", label: "French" },
    { value: "German", label: "German" },
    { value: "Malay", label: "Malay" },
    { value: "Albanian", label: "Albanian" },
    { value: "Bosnian", label: "Bosnian" },
    { value: "Urdu", label: "Urdu" },
  ];

  return (
    <div className="d-flex flex-column">
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Row className="mb-4 shadow-sm rounded-4 py-3">
          <Col md={12}>
            <SectionHeader
              title="Age Verification Information"
              tooltip="We ask for your date of birth and ID upload to confirm you’re under 21, unlocking exclusive youth membership benefits. Your ID is securely verified and deleted after processing to protect your privacy."
            />
          </Col>
          <Col md={6}>
            <Form.Group controlId="dob">
              <Form.Label>Date of Birth*</Form.Label>
              <Form.Control
                disabled={!!disabledField}
                type="date"
                {...register("dateOfBirth")}
                defaultValue={
                  defaultData?.dateOfBirth
                    ? new Date(defaultData.dateOfBirth)
                        .toISOString()
                        .split("T")[0]
                    : undefined
                }
                isInvalid={!!errors.dateOfBirth}
              />
              <Form.Control.Feedback
                className="custom-error-text px-1"
                type="invalid"
              >
                {errors.dateOfBirth?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="idUpload">
              <OverlayTrigger
                placement="right"
                overlay={renderTooltip(
                  "PNG, JPG, JPEG, HEIF, HEIC, PDF, Max 5MB"
                )}
              >
                <Form.Label>ID Upload*</Form.Label>
              </OverlayTrigger>
              <Form.Control
                type="file"
                accept=".png,.jpg,.jpeg,.pdf,.heif,.heic"
                onChange={(e) => {
                  const file = (e.target as HTMLInputElement).files?.[0];
                  if (file) {
                    const maxSize = 5 * 1024 * 1024; // 5MB
                    const validTypes = [
                      "image/png",
                      "image/jpeg",
                      "image/heif",
                      "image/heic",
                      "application/pdf",
                    ];

                    if (!validTypes.includes(file.type)) {
                      setError("idDocument", {
                        type: "manual",
                        message: "Unsupported File Format",
                      });
                      return;
                    }

                    if (file.size > maxSize) {
                      setError("idDocument", {
                        type: "manual",
                        message: "File size must be less than 5MB",
                      });
                      return;
                    }

                    const reader = new FileReader();
                    reader.onloadend = () => {
                      const base64String = reader.result?.toString();
                      if (base64String) {
                        clearErrors("idDocument");
                        const event = {
                          target: {
                            name: "idDocument",
                            value: base64String,
                          },
                        };
                        register("idDocument").onChange(event);
                      } else {
                        setError("idDocument", {
                          type: "manual",
                          message: "File upload failed. Please try again.",
                        });
                      }
                    };
                    reader.onerror = () => {
                      setError("idDocument", {
                        type: "manual",
                        message: "File upload failed. Please try again.",
                      });
                    };

                    reader.readAsDataURL(file);
                  }
                }}
                isInvalid={!!errors.idDocument}
              />
              <Form.Control.Feedback
                className="custom-error-text px-1"
                type="invalid"
              >
                {errors.idDocument?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-4 shadow-sm rounded-4 py-3">
          <Col md={12}>
            <SectionHeader
              title="Contact Information"
              tooltip="Sharing your city and country helps us connect you with mentors and peers in your region. A phone number is optional but allows additional contact options for networking opportunities."
            />
          </Col>
          <Col md={4}>
            <Form.Group controlId="phoneNumber">
              <Form.Label>Phone Number*</Form.Label>
              <div className="form-control p-0">
                <Controller
                  control={control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <PhoneInput
                      {...field}
                      placeholder="Enter phone number"
                      defaultCountry="GB"
                      value={field.value || ""}
                      onChange={(value) => {
                        field.onChange(value);
                      }}
                      className="phone-input w-100"
                    />
                  )}
                />
              </div>
              {errors.phoneNumber && (
                <div className="custom-error-text px-1 ">
                  {errors.phoneNumber.message}
                </div>
              )}
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group controlId="city">
              <Form.Label>City*</Form.Label>
              <Form.Control
                disabled={!!disabledField}
                type="text"
                {...register("city")}
                isInvalid={!!errors.city}
              />
              <Form.Control.Feedback
                className="custom-error-text px-1"
                type="invalid"
              >
                {errors.city?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group controlId="country">
              <SelectInput
                name="country"
                label="Country"
                placeholder="Select Country"
                control={control}
                options={countryOptions}
                error={errors.country}
                isRequired
                disabled={!!disabledField}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-4 shadow-sm rounded-4 py-3">
          <Col md={12}>
            <SectionHeader
              title="Personal Profile Information"
              tooltip='Your gender and language preferences help us personalise your experience. The "About You" section and professional photo make your profile engaging, helping mentors and peers connect with you.'
            />
          </Col>
          <Col md={4}>
            <Form.Group controlId="gender">
              <Form.Label>Gender*</Form.Label>
              <Form.Select
                {...register("gender")}
                isInvalid={!!errors.gender}
                defaultValue={defaultData?.gender || ""}
              >
                <option value="" disabled hidden>
                  Select gender...
                </option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </Form.Select>
              <Form.Control.Feedback
                className="custom-error-text px-1"
                type="invalid"
              >
                {errors.gender?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group controlId="languagePreference">
              <SelectInput
                name="languagePreference"
                label="Language Preference"
                placeholder="Select Language"
                control={control}
                options={languageOptions}
                error={errors.languagePreference}
                isRequired
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group controlId="professionalPhoto">
              <OverlayTrigger
                placement="right"
                overlay={renderTooltip(
                  "PNG, JPG, JPEG, HEIF, HEIC, PDF, Min 200x200px, Max 5MB"
                )}
              >
                <Form.Label>Professional Photo</Form.Label>
              </OverlayTrigger>
              <Form.Control
                type="file"
                accept=".png,.jpg,.jpeg"
                onChange={(e) => {
                  const file = (e.target as HTMLInputElement).files?.[0];
                  if (!file) return;

                  const maxSize = 5 * 1024 * 1024;
                  const validTypes = ["image/png", "image/jpeg"];

                  if (!validTypes.includes(file.type)) {
                    setError("professionalPhoto", {
                      type: "manual",
                      message: "Unsupported File Format",
                    });
                    return;
                  }

                  if (file.size > maxSize) {
                    setError("professionalPhoto", {
                      type: "manual",
                      message: "File must be less than 5MB",
                    });
                    return;
                  }

                  const reader = new FileReader();
                  reader.onloadend = () => {
                    const base64 = reader.result as string;
                    setTempImage(base64);
                    setShowCropper(true);
                  };
                  reader.readAsDataURL(file);
                }}
                isInvalid={!!errors.professionalPhoto}
              />

              {errors.professionalPhoto?.message && (
                <Form.Control.Feedback
                  className="custom-error-text px-1"
                  type="invalid"
                >
                  {errors.professionalPhoto?.message}
                </Form.Control.Feedback>
              )}

              {/* Cropper Modal */}
              {tempImage && (
                <ImageCropperModal
                  show={showCropper}
                  imageSrc={tempImage}
                  aspectRatio={1} // 1:1
                  onClose={() => setShowCropper(false)}
                  onSave={(croppedFile, base64) => {
                    const img = new Image();
                    img.onload = () => {
                      if (img.width < 200 || img.height < 200) {
                        setError("professionalPhoto", {
                          type: "manual",
                          message: "Cropped image must be at least 200x200px",
                        });
                        return;
                      }

                      clearErrors("professionalPhoto");

                      // Save cropped file to state
                      setCroppedPhotoFile(croppedFile);

                      // Optionally store base64 if needed for preview
                      setShowCropper(false);
                      setTempImage(null);
                    };
                    img.src = base64;
                  }}
                />
              )}
              <Form.Control.Feedback
                className="custom-error-text px-1"
                type="invalid"
              >
                {errors.professionalPhoto?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Form.Group controlId="aboutYou" className="mb-4 mt-3">
            <Form.Label>About You*</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              {...register("aboutYou")}
              isInvalid={!!errors.aboutYou}
            />
            <Form.Control.Feedback
              className="custom-error-text px-1"
              type="invalid"
            >
              {errors.aboutYou?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Row>

        <Row className="mb-4 shadow-sm rounded-4 py-3">
          <Col md={12}>
            <SectionHeader
              title="Education and Interests"
              tooltip="Sharing your education level, field of interest, and skills helps us understand your background and goals. This information allows us to match you with mentors who can guide you in areas you’re passionate about."
            />
          </Col>
          <Col md={6}>
            <Form.Group controlId="educationLevel">
              <Form.Label>Current Education Level*</Form.Label>
              <Form.Select
                {...register("educationLevel")}
                isInvalid={!!errors.educationLevel}
                defaultValue={defaultData?.educationLevel || ""}
              >
                <option value="" disabled hidden>
                  Select Education Level...
                </option>
                {[
                  "College",
                  "Other",
                  "School",
                  "Self-Taught",
                  "University",
                ].map((item, idx) => (
                  <option value={item} key={idx}>
                    {item}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback
                className="custom-error-text px-1"
                type="invalid"
              >
                {errors.educationLevel?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="fieldOfInterest">
              <Form.Label>Field of Study or Interest*</Form.Label>
              <Form.Select
                {...register("studyInterest")}
                isInvalid={!!errors.studyInterest}
                defaultValue={defaultData?.studyInterest || ""}
              >
                <option value="" disabled hidden>
                  Select Field of Study or Interest...
                </option>
                {[
                  "Agriculture & Food Production",
                  "Construction & Real Estate",
                  "Consumer Services",
                  "Education & Training",
                  "Energy & Utilities",
                  "Financial Services",
                  "Healthcare & Life Sciences",
                  "Hospitality & Tourism",
                  "Manufacturing & Industrial",
                  "Media & Entertainment",
                  "Non-Profit & Social Enterprises",
                  "Other",
                  "Professional Services",
                  "Retail & E-Commerce",
                  "Technology & Software",
                  "Transportation & Logistics",
                ].map((item, idx) => (
                  <option value={item} key={idx}>
                    {item}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback
                className="custom-error-text px-1"
                type="invalid"
              >
                {errors.studyInterest?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={12}>
            <Form.Group controlId="skills" className="mb-3 mt-3">
              <Form.Label>Skills or Experience</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                {...register("skillsExperience")}
              />
            </Form.Group>
          </Col>
          <Col md={12}>
            <Form.Group controlId="universityCollege" className="mb-3 mt-3">
              <Form.Label>University/College*</Form.Label>

              {selectedUniversity === "Other" || hasOther ? (
                // If "Other" is selected, change to an input field
                <Form.Control
                  type="text"
                  placeholder="Please specify"
                  {...register("universityCollege")}
                  isInvalid={!!errors.universityCollege}
                />
              ) : (
                // Otherwise, show the dropdown
                <Form.Control
                  as="select"
                  {...register("universityCollege")}
                  isInvalid={!!errors.universityCollege}
                  value={selectedUniversity}
                  onChange={handleUniversityChange}
                >
                  <option value="" disabled hidden>
                    Please select your Institution of learning
                  </option>
                  {universities.map((item, idx) => (
                    <option value={item} key={idx}>
                      {item}
                    </option>
                  ))}
                </Form.Control>
              )}
              <Form.Control.Feedback
                className="custom-error-text px-1"
                type="invalid"
              >
                {errors.universityCollege?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-4 shadow-sm rounded-4 py-3">
          <Col md={12}>
            <SectionHeader
              title="Mentorship Goals"
              tooltip="Telling us your mentorship goals and preferred mentor expertise helps us find the right mentor to support your aspirations."
            />
          </Col>
          <Form.Group controlId="objectives" className="mb-3">
            <Form.Label>Mentorship Objectives*</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              {...register("mentorshipObjectives")}
              isInvalid={!!errors.mentorshipObjectives}
            />
            <Form.Control.Feedback
              className="custom-error-text px-1"
              type="invalid"
            >
              {errors.mentorshipObjectives?.message}
            </Form.Control.Feedback>
          </Form.Group>
          <Col md={6}>
            <Form.Group controlId="mentorshipExpertise">
              <Form.Label>Preferred Mentor Expertise*</Form.Label>
              <Form.Select
                {...register("mentorshipExpertise")}
                isInvalid={!!errors.mentorshipExpertise}
                defaultValue={defaultData?.mentorshipExpertise || ""}
              >
                <option value="" disabled hidden>
                  Select Preferred Mentor Expertise...
                </option>
                {[
                  "Agriculture & Food Production",
                  "Construction & Real Estate",
                  "Consumer Services",
                  "Education & Training",
                  "Energy & Utilities",
                  "Financial Services",
                  "Healthcare & Life Sciences",
                  "Hospitality & Tourism",
                  "Manufacturing & Industrial",
                  "Media & Entertainment",
                  "Non-Profit & Social Enterprises",
                  "Other",
                  "Professional Services",
                  "Retail & E-Commerce",
                  "Technology & Software",
                  "Transportation & Logistics",
                ].map((item, idx) => (
                  <option value={item} key={idx}>
                    {item}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback
                className="custom-error-text px-1"
                type="invalid"
              >
                {errors.mentorshipExpertise?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="mentorshipFormat">
              <Form.Label>Preferred Mentorship Format*</Form.Label>
              <Form.Select
                {...register("mentorshipFormat")}
                isInvalid={!!errors.mentorshipFormat}
                defaultValue={defaultData?.mentorshipFormat || ""}
              >
                <option value="" disabled hidden>
                  Select Preferred Mentorship Format...
                </option>
                <option value="Hybrid">Hybrid</option>
                <option value="In-Person">In-Person</option>
                <option value="Virtual">Virtual</option>
              </Form.Select>
              <Form.Control.Feedback
                className="custom-error-text px-1"
                type="invalid"
              >
                {errors.mentorshipFormat?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-4 shadow-sm rounded-4 py-3">
          <Col md={12}>
            <SectionHeader
              title="Platform Engagement Preferences"
              tooltip="Sharing your interests helps us recommend exciting activities."
            />
          </Col>
          <Form.Group controlId="platformInterest" className="mb-3">
            <Form.Label>Areas of Interest on the Platform*</Form.Label>
            <Form.Control
              type="text"
              {...register("platformInterest")}
              isInvalid={!!errors.platformInterest}
            />
            <Form.Control.Feedback
              className="custom-error-text px-1"
              type="invalid"
            >
              {errors.platformInterest?.message}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group controlId="peerNetworking" className="mb-4">
            <Form.Label>Are You Interested in Peer Networking?*</Form.Label>
            <Form.Select
              {...register("peerNetworking")}
              isInvalid={!!errors.peerNetworking}
              defaultValue={defaultData?.peerNetworking || ""}
            >
              <option value="" hidden disabled>
                Select Peer Networking Option...
              </option>
              <option value={"Yes"}>Yes</option>
              <option value={"No"}>No</option>
            </Form.Select>
            <Form.Control.Feedback
              className="custom-error-text px-1"
              type="invalid"
            >
              {errors.peerNetworking?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Row>

        <div className="d-flex justify-content-end">
          <Button disabled={isPending} variant="primary" type="submit">
            {isPending ? "Saving..." : "Save"}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default EditYouthData;
